import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../api/client.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('farmavida_token'));

  // Set up axios defaults - Remove this as we're using apiClient
  useEffect(() => {
    // Token is already handled by apiClient interceptor
    // No need to set axios defaults
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await apiClient.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Token validation failed:', error);
          // Token is invalid or expired
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      
      // Store token
      localStorage.setItem('farmavida_token', access_token);
      setToken(access_token);
      setUser(userData);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      
      const { access_token, user: newUser } = response.data;
      
      // Store token
      localStorage.setItem('farmavida_token', access_token);
      setToken(access_token);
      setUser(newUser);

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al registrar usuario'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('farmavida_token');
    setToken(null);
    setUser(null);
  };

  const createAdmin = async () => {
    try {
      const response = await apiClient.post('/auth/create-admin');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Create admin error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al crear administrador'
      };
    }
  };

  const loginWithToken = (token, tenantId) => {
    localStorage.setItem('farmavida_token', token);
    localStorage.setItem('tenant_id', tenantId);
    setToken(token);
    // Obtener info del usuario con el token
    apiClient.get('/auth/me').then(res => {
      setUser(res.data);
      window.history.replaceState({}, '', '/');
    }).catch(() => {
      // Si falla, intentar decodificar el JWT localmente
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ id: payload.sub, email: payload.email, full_name: payload.company_name, role: 'admin' });
        window.history.replaceState({}, '', '/');
      } catch(e) {
        localStorage.removeItem('farmavida_token');
      }
    });
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    createAdmin,
    loginWithToken,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;